import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
    LayoutDashboard, Inbox, Globe, LogOut, MapPin, Calendar, Users, DollarSign,
    ChevronRight, Clock, FileText, Send, Eye, Loader2, Menu, MessageCircle,
    Search, Building2, ChevronLeft, Star, Sparkles, Lock, Unlock, Check, X,
    ArrowUpRight, Plus, Trash2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TeamDashboard() {
    const [activeView, setActiveView] = useState('dashboard');
    const [context, setContext] = useState(null);
    const [stats, setStats] = useState(null);
    const [leads, setLeads] = useState([]);
    const [itineraries, setItineraries] = useState([]);
    const [conversations, setConversations] = useState([]);
    const [unlockedLeads, setUnlockedLeads] = useState([]);
    const [rejectedLeads, setRejectedLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [leadSearch, setLeadSearch] = useState('');
    const [unlocking, setUnlocking] = useState(null);
    const [selectedLead, setSelectedLead] = useState(null);
    const [leadProposal, setLeadProposal] = useState(null);
    const [previewTab, setPreviewTab] = useState('details');
    // Messaging
    const [activeConversation, setActiveConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [sendingMsg, setSendingMsg] = useState(false);
    const wsRef = useRef(null);
    const msgEndRef = useRef(null);

    const { user, logout, api } = useAuth();
    const navigate = useNavigate();

    const teamRoles = user?.team_roles || [];
    const isLeadsAgent = teamRoles.includes('leads_agent');
    const isContent = teamRoles.includes('content_marketing');

    const fetchAll = useCallback(async () => {
        try {
            const [ctxRes, statsRes] = await Promise.allSettled([
                api.get('/team/context'),
                api.get('/team/stats')
            ]);
            if (ctxRes.status === 'fulfilled') setContext(ctxRes.value.data);
            if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);

            if (isLeadsAgent) {
                const [leadsRes, ulRes, rejRes, convRes] = await Promise.allSettled([
                    api.get('/team/leads'),
                    api.get('/operator/leads/unlocked'),
                    api.get('/operator/leads/rejected'),
                    api.get('/team/conversations')
                ]);
                if (leadsRes.status === 'fulfilled') setLeads(leadsRes.value.data);
                if (ulRes.status === 'fulfilled') setUnlockedLeads(ulRes.value.data);
                if (rejRes.status === 'fulfilled') setRejectedLeads(rejRes.value.data);
                if (convRes.status === 'fulfilled') setConversations(convRes.value.data);
            }
            if (isContent) {
                const itinRes = await api.get('/team/itineraries');
                setItineraries(itinRes.data);
            }
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [api, isLeadsAgent, isContent]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const unlockLead = async (tripId) => {
        setUnlocking(tripId);
        try {
            const res = await api.post(`/operator/leads/${tripId}/unlock`);
            if (res.data.already_unlocked) { toast.info('Lead already unlocked'); }
            else { toast.success(`Lead unlocked for ${res.data.cost} credits`); }
            fetchAll();
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed to unlock'); }
        finally { setUnlocking(null); }
    };

    const rejectLead = async (tripId) => {
        try { await api.post(`/operator/leads/${tripId}/reject`); toast.success('Lead hidden'); fetchAll(); }
        catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    };

    // Messaging
    const openConversation = async (conv) => {
        setActiveConversation(conv);
        setActiveView('chat');
        try {
            const res = await api.get(`/conversations/${conv.id}/messages`);
            setMessages(res.data);
            await api.post(`/conversations/${conv.id}/read`);
        } catch {}
        // Connect WebSocket
        if (wsRef.current) wsRef.current.close();
        const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
        const token = localStorage.getItem('karibu_token');
        const ws = new WebSocket(`${wsUrl}/ws/chat/${conv.id}?token=${token}`);
        ws.onmessage = (event) => {
            const msg = JSON.parse(event.data);
            if (msg.type === 'message') { setMessages(prev => [...prev, msg]); }
        };
        wsRef.current = ws;
    };

    const sendMessage = async () => {
        if (!newMessage.trim() || !activeConversation) return;
        setSendingMsg(true);
        try {
            await api.post(`/conversations/${activeConversation.id}/messages`, { content: newMessage.trim() });
            setNewMessage('');
        } catch { toast.error('Failed to send'); }
        finally { setSendingMsg(false); }
    };

    useEffect(() => { msgEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
    useEffect(() => { return () => { if (wsRef.current) wsRef.current.close(); }; }, []);

    const loadProposal = async (tripId) => {
        try {
            const res = await api.get(`/itineraries/trip/${tripId}`);
            const mine = res.data.filter(it => it.operator_id === context?.operator_id);
            setLeadProposal(mine.length > 0 ? mine[0] : null);
        } catch { setLeadProposal(null); }
    };

    const handleLogout = () => { logout(); navigate('/'); };
    const fmt = (d) => { try { return format(new Date(d), 'MMM dd, yyyy'); } catch { return d || 'N/A'; } };

    if (loading) return (
        <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center"><Loader2 size={28} className="animate-spin text-neutral-400" /></div>
    );

    const visibleLeads = leads
        .filter(l => !rejectedLeads.includes(l.id))
        .filter(l => (l.traveler_name || '').toLowerCase().includes(leadSearch.toLowerCase()) ||
            (l.destination_interests || []).join(' ').toLowerCase().includes(leadSearch.toLowerCase()));

    const NAV = [
        { id: 'dashboard', label: 'Overview', icon: LayoutDashboard },
        ...(isLeadsAgent ? [
            { id: 'leads', label: 'Leads', icon: Inbox, count: visibleLeads.length },
            { id: 'messages', label: 'Messaging', icon: MessageCircle, count: conversations.filter(c => c.last_message && !c.last_message.read).length },
        ] : []),
        ...(isContent ? [
            { id: 'itineraries', label: 'Curated Itineraries', icon: Globe, count: itineraries.length },
        ] : []),
    ];

    return (
        <div className="min-h-screen bg-[#FAFAF9] flex" data-testid="team-dashboard">
            {/* Sidebar */}
            <aside className="hidden lg:flex flex-col w-56 bg-zinc-900 fixed h-full z-30" data-testid="team-sidebar">
                <div className="px-5 pt-6 pb-4">
                    <Link to="/" className="flex items-center"><img src="/safarinodes-logo.png" alt="safarinodes" className="h-7 w-auto" /></Link>
                    <p className="text-[10px] text-zinc-500 mt-1 font-medium uppercase tracking-wider">Team Portal</p>
                </div>

                {context && (
                    <div className="mx-3 mb-3 bg-white/5 rounded-lg px-3 py-2.5">
                        <p className="text-xs font-semibold text-white truncate">{context.company_name}</p>
                        <div className="flex gap-1 mt-1.5">
                            {teamRoles.map(r => (
                                <span key={r} className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
                                    r === 'leads_agent' ? 'bg-blue-500/20 text-blue-300' : 'bg-purple-500/20 text-purple-300'
                                }`}>{r === 'leads_agent' ? 'Leads' : 'Content'}</span>
                            ))}
                        </div>
                    </div>
                )}

                <nav className="flex-1 px-3 space-y-0.5">
                    {NAV.map(item => (
                        <button key={item.id} onClick={() => { setActiveView(item.id); setSelectedLead(null); }}
                            className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                                activeView === item.id || (activeView === 'chat' && item.id === 'messages') ? 'bg-white/10 text-white' : 'text-zinc-400 hover:text-white hover:bg-white/5'
                            }`} data-testid={`team-nav-${item.id}`}>
                            <item.icon size={17} strokeWidth={1.8} />
                            <span className="flex-1 text-left">{item.label}</span>
                            {item.count > 0 && (
                                <span className="min-w-[20px] h-5 px-1.5 text-[10px] font-bold rounded-full bg-white/20 text-white flex items-center justify-center">{item.count}</span>
                            )}
                        </button>
                    ))}
                </nav>

                <div className="px-3 pb-4 border-t border-zinc-800 pt-3">
                    <div className="flex items-center gap-2 px-3 py-2 mb-2">
                        <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                            {(user?.name || 'T').charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-white truncate">{user?.name}</p>
                            <p className="text-[10px] text-zinc-500">Team Member</p>
                        </div>
                    </div>
                    <button onClick={handleLogout} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-zinc-400 hover:text-red-400 hover:bg-red-400/10 transition" data-testid="team-logout">
                        <LogOut size={15} /> Sign Out
                    </button>
                </div>
            </aside>

            {/* Mobile Header */}
            <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-zinc-900">
                <div className="px-4 py-3 flex items-center justify-between">
                    <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 text-zinc-400 hover:text-white"><Menu size={20} /></button>
                    <span className="text-base font-bold text-white">Team Portal</span>
                    <button onClick={handleLogout} className="p-2 text-zinc-400 hover:text-red-400"><LogOut size={18} /></button>
                </div>
                {mobileMenuOpen && (
                    <div className="border-t border-zinc-800 bg-zinc-900 px-4 py-3 flex gap-2 overflow-x-auto">
                        {NAV.map(item => (
                            <button key={item.id} onClick={() => { setActiveView(item.id); setMobileMenuOpen(false); }}
                                className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium whitespace-nowrap ${
                                    activeView === item.id ? 'bg-white text-zinc-900' : 'bg-zinc-800 text-zinc-300'
                                }`}><item.icon size={14} /> {item.label}</button>
                        ))}
                    </div>
                )}
            </div>

            {/* Main Content */}
            <div className="flex-1 lg:ml-56 pt-14 lg:pt-0">
                <main className="p-6 lg:p-8 max-w-6xl mx-auto">

                    {/* ===== OVERVIEW ===== */}
                    {activeView === 'dashboard' && (
                        <div data-testid="team-overview">
                            <div className="mb-6">
                                <h1 className="text-2xl font-bold text-zinc-900">Welcome, {user?.name}</h1>
                                <p className="text-sm text-zinc-400 mt-1">{context?.company_name} &middot; Team Member</p>
                            </div>

                            <div className="bg-zinc-900 rounded-2xl px-6 py-5 mb-6" data-testid="team-stats-banner">
                                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                                    {[
                                        ...(isLeadsAgent ? [
                                            { label: 'Total Leads', value: stats?.total_leads || 0, color: 'text-white' },
                                            { label: 'Proposals Sent', value: stats?.proposals_sent || 0, color: 'text-blue-400' },
                                            { label: 'Bookings', value: stats?.bookings || 0, color: 'text-emerald-400' },
                                        ] : []),
                                        ...(isContent ? [
                                            { label: 'Curated', value: stats?.curated_itineraries || 0, color: 'text-purple-400' },
                                            { label: 'Published', value: stats?.published_itineraries || 0, color: 'text-amber-400' },
                                        ] : []),
                                    ].map(stat => (
                                        <div key={stat.label} className="text-center">
                                            <p className={`text-xl font-bold ${stat.color}`}>{stat.value}</p>
                                            <p className="text-[10px] text-zinc-400 font-medium uppercase tracking-wide mt-0.5">{stat.label}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="grid sm:grid-cols-2 gap-4">
                                {isLeadsAgent && (
                                    <>
                                        <button onClick={() => setActiveView('leads')} className="bg-white rounded-xl border border-zinc-100 p-6 text-left hover:shadow-sm transition group" data-testid="quick-leads">
                                            <Inbox size={20} className="text-zinc-400 group-hover:text-blue-500 mb-3 transition" />
                                            <p className="text-sm font-semibold text-zinc-900">Manage Leads</p>
                                            <p className="text-xs text-zinc-400 mt-0.5">{visibleLeads.length} active leads</p>
                                        </button>
                                        <button onClick={() => setActiveView('messages')} className="bg-white rounded-xl border border-zinc-100 p-6 text-left hover:shadow-sm transition group" data-testid="quick-messages">
                                            <MessageCircle size={20} className="text-zinc-400 group-hover:text-blue-500 mb-3 transition" />
                                            <p className="text-sm font-semibold text-zinc-900">Messaging</p>
                                            <p className="text-xs text-zinc-400 mt-0.5">{conversations.length} conversations</p>
                                        </button>
                                    </>
                                )}
                                {isContent && (
                                    <>
                                        <button onClick={() => setActiveView('itineraries')} className="bg-white rounded-xl border border-zinc-100 p-6 text-left hover:shadow-sm transition group" data-testid="quick-itineraries">
                                            <Globe size={20} className="text-zinc-400 group-hover:text-purple-500 mb-3 transition" />
                                            <p className="text-sm font-semibold text-zinc-900">Curated Itineraries</p>
                                            <p className="text-xs text-zinc-400 mt-0.5">{itineraries.length} itineraries</p>
                                        </button>
                                        <button onClick={() => navigate('/operator/create-curated')} className="bg-white rounded-xl border border-zinc-100 p-6 text-left hover:shadow-sm transition group" data-testid="quick-create-curated">
                                            <Plus size={20} className="text-zinc-400 group-hover:text-purple-500 mb-3 transition" />
                                            <p className="text-sm font-semibold text-zinc-900">Create Itinerary</p>
                                            <p className="text-xs text-zinc-400 mt-0.5">Build a new curated itinerary</p>
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {/* ===== LEADS ===== */}
                    {activeView === 'leads' && isLeadsAgent && !selectedLead && (
                        <div data-testid="team-leads-view">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <h2 className="text-xl font-bold text-zinc-900">Incoming Leads</h2>
                                    <p className="text-sm text-zinc-400 mt-1">{visibleLeads.length} lead{visibleLeads.length !== 1 ? 's' : ''} available</p>
                                </div>
                                <div className="relative">
                                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                                    <input type="text" value={leadSearch} onChange={e => setLeadSearch(e.target.value)} placeholder="Search leads..."
                                        className="pl-9 pr-4 py-2 rounded-lg border border-zinc-200 text-sm w-56 focus:border-zinc-400 focus:ring-0 bg-white" data-testid="team-lead-search" />
                                </div>
                            </div>

                            {visibleLeads.length === 0 ? (
                                <div className="text-center py-20 bg-white rounded-xl border border-zinc-100">
                                    <Inbox size={36} className="mx-auto text-zinc-200 mb-3" />
                                    <p className="text-zinc-500">No matching leads right now</p>
                                </div>
                            ) : (
                                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                                    {visibleLeads.map(lead => {
                                        const isUnlocked = unlockedLeads.includes(lead.id);
                                        return (
                                            <div key={lead.id} className="bg-white rounded-xl border border-zinc-100 p-5 hover:shadow-sm transition" data-testid={`team-lead-${lead.id}`}>
                                                <div className="flex items-start justify-between mb-3">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-600 font-bold text-sm">
                                                            {(lead.traveler_name || 'T').charAt(0).toUpperCase()}
                                                        </div>
                                                        <div>
                                                            <h3 className="font-semibold text-zinc-900 text-sm">{isUnlocked ? lead.traveler_name : 'Locked Lead'}</h3>
                                                            {lead.lead_type === 'curated' && <Badge className="bg-amber-50 text-amber-700 border border-amber-200 text-[9px] mt-0.5">Curated</Badge>}
                                                        </div>
                                                    </div>
                                                    <Badge className={lead.status === 'responded' ? 'bg-purple-50 text-purple-700 border border-purple-200' : 'bg-blue-50 text-blue-700 border border-blue-200'}>
                                                        {lead.status}
                                                    </Badge>
                                                </div>
                                                <div className="space-y-1.5 text-xs text-zinc-500 mb-4">
                                                    <p className="flex items-center gap-1"><MapPin size={11} /> {lead.destination_interests?.join(', ')}</p>
                                                    <p className="flex items-center gap-1"><Calendar size={11} /> {fmt(lead.travel_dates?.start)} — {fmt(lead.travel_dates?.end)}</p>
                                                    <p className="flex items-center gap-1"><Users size={11} /> {lead.group_size} guests &middot; ${lead.budget_range?.min}–${lead.budget_range?.max}</p>
                                                </div>
                                                <div className="flex gap-2">
                                                    {isUnlocked || lead.lead_type === 'curated' ? (
                                                        <>
                                                            <button onClick={() => { setSelectedLead(lead); setPreviewTab('details'); loadProposal(lead.id); }}
                                                                className="flex-1 bg-zinc-900 text-white hover:bg-zinc-800 rounded-lg py-2 text-xs font-medium transition" data-testid={`view-lead-${lead.id}`}>
                                                                <Eye size={13} className="inline mr-1" /> View Details
                                                            </button>
                                                            <button onClick={() => rejectLead(lead.id)} className="p-2 rounded-lg border border-zinc-200 text-zinc-400 hover:text-red-500 hover:border-red-200 transition" data-testid={`reject-lead-${lead.id}`}>
                                                                <X size={14} />
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <button onClick={() => unlockLead(lead.id)} disabled={unlocking === lead.id}
                                                            className="flex-1 bg-zinc-900 text-white hover:bg-zinc-800 rounded-lg py-2 text-xs font-medium disabled:opacity-50 transition flex items-center justify-center gap-1.5" data-testid={`unlock-lead-${lead.id}`}>
                                                            {unlocking === lead.id ? <Loader2 size={13} className="animate-spin" /> : <Lock size={13} />}
                                                            {unlocking === lead.id ? 'Unlocking...' : 'Unlock Lead'}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ===== LEAD DETAIL ===== */}
                    {activeView === 'leads' && selectedLead && (
                        <div data-testid="team-lead-detail">
                            <button onClick={() => setSelectedLead(null)} className="flex items-center gap-1 text-sm text-zinc-500 mb-4 hover:text-zinc-900">
                                <ChevronLeft size={16} /> Back to Leads
                            </button>
                            <div className="bg-white rounded-xl border border-zinc-100 p-6 mb-4">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center gap-4">
                                        <div className="w-14 h-14 rounded-2xl bg-zinc-100 flex items-center justify-center text-zinc-600 text-2xl font-bold">
                                            {(selectedLead.traveler_name || 'T').charAt(0).toUpperCase()}
                                        </div>
                                        <div>
                                            <h2 className="text-lg font-bold text-zinc-900">{selectedLead.traveler_name}</h2>
                                            <p className="text-sm text-zinc-400">{selectedLead.destination_interests?.join(', ')}</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={() => setPreviewTab('details')} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${previewTab === 'details' ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-600'}`}>Details</button>
                                        <button onClick={() => setPreviewTab('proposal')} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${previewTab === 'proposal' ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-600'}`}>Proposal</button>
                                    </div>
                                </div>
                                {previewTab === 'details' && (
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        {[
                                            { label: 'Travel Dates', value: `${fmt(selectedLead.travel_dates?.start)} — ${fmt(selectedLead.travel_dates?.end)}`, icon: Calendar },
                                            { label: 'Group Size', value: `${selectedLead.group_size} travelers`, icon: Users },
                                            { label: 'Budget', value: `$${selectedLead.budget_range?.min} – $${selectedLead.budget_range?.max}`, icon: DollarSign },
                                            { label: 'Status', value: selectedLead.status, icon: Clock },
                                        ].map((item, i) => (
                                            <div key={i} className="bg-zinc-50 rounded-lg p-4">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <item.icon size={14} className="text-zinc-400" />
                                                    <p className="text-xs text-zinc-400">{item.label}</p>
                                                </div>
                                                <p className="text-sm font-semibold text-zinc-900">{item.value}</p>
                                            </div>
                                        ))}
                                        {selectedLead.preferences?.length > 0 && (
                                            <div className="sm:col-span-2 bg-zinc-50 rounded-lg p-4">
                                                <p className="text-xs text-zinc-400 mb-2">Preferences</p>
                                                <div className="flex flex-wrap gap-1.5">
                                                    {selectedLead.preferences.map(p => (
                                                        <span key={p} className="text-[10px] bg-white text-zinc-600 px-2 py-0.5 rounded-full border border-zinc-200">{p}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {selectedLead.special_requests && (
                                            <div className="sm:col-span-2 bg-zinc-50 rounded-lg p-4">
                                                <p className="text-xs text-zinc-400 mb-1">Special Requests</p>
                                                <p className="text-sm text-zinc-700">{selectedLead.special_requests}</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {previewTab === 'proposal' && (
                                    <div>
                                        {leadProposal ? (
                                            <div className="bg-zinc-50 rounded-xl p-5">
                                                <div className="flex items-center justify-between mb-3">
                                                    <h3 className="font-semibold text-zinc-900">{leadProposal.title}</h3>
                                                    <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">Submitted</Badge>
                                                </div>
                                                <p className="text-sm text-zinc-500 mb-3">{leadProposal.description}</p>
                                                <div className="flex gap-4 text-xs text-zinc-500">
                                                    <span>{leadProposal.duration_days} days</span>
                                                    <span>${leadProposal.total_price}</span>
                                                    <span>{fmt(leadProposal.created_at)}</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="text-center py-10">
                                                <FileText size={28} className="mx-auto text-zinc-200 mb-3" />
                                                <p className="text-zinc-500 mb-3">No proposal submitted yet</p>
                                                <Button onClick={() => navigate(`/operator/create-itinerary/${selectedLead.id}`)}
                                                    className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-lg px-5 py-2.5 text-sm font-medium" data-testid="create-proposal-btn">
                                                    <FileText size={14} className="mr-1.5" /> Create Proposal
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            {!leadProposal && previewTab === 'details' && (
                                <Button onClick={() => navigate(`/operator/create-itinerary/${selectedLead.id}`)}
                                    className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-lg px-5 py-2.5 text-sm font-medium" data-testid="create-proposal-from-details">
                                    <FileText size={14} className="mr-1.5" /> Create Proposal
                                </Button>
                            )}
                        </div>
                    )}

                    {/* ===== CURATED ITINERARIES ===== */}
                    {activeView === 'itineraries' && isContent && (
                        <div data-testid="team-itineraries-view">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <h2 className="text-xl font-bold text-zinc-900">Curated Itineraries</h2>
                                    <p className="text-sm text-zinc-400 mt-1">{itineraries.length} itinerar{itineraries.length !== 1 ? 'ies' : 'y'}</p>
                                </div>
                                <Button onClick={() => navigate('/operator/create-curated')} className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-lg px-4 py-2 text-sm font-medium" data-testid="team-create-curated">
                                    <Plus size={15} className="mr-1" /> Create Itinerary
                                </Button>
                            </div>

                            {itineraries.length === 0 ? (
                                <div className="text-center py-20 bg-white rounded-xl border border-zinc-100">
                                    <Globe size={36} className="mx-auto text-zinc-200 mb-3" />
                                    <p className="text-zinc-500 mb-3">No curated itineraries yet</p>
                                    <Button onClick={() => navigate('/operator/create-curated')} className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-lg px-5 py-2.5 text-sm">
                                        Create Your First
                                    </Button>
                                </div>
                            ) : (
                                <div className="grid md:grid-cols-2 gap-4">
                                    {itineraries.map(it => (
                                        <div key={it.id} className="bg-white rounded-xl border border-zinc-100 p-5" data-testid={`team-itin-${it.id}`}>
                                            <div className="flex items-start gap-4">
                                                {it.cover_image ? (
                                                    <img src={it.cover_image} alt="" className="w-16 h-16 rounded-xl object-cover flex-shrink-0" />
                                                ) : (
                                                    <div className="w-16 h-16 rounded-xl bg-zinc-100 flex items-center justify-center flex-shrink-0"><Globe size={20} className="text-zinc-300" /></div>
                                                )}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <h3 className="font-semibold text-zinc-900 text-sm truncate">{it.title || 'Untitled'}</h3>
                                                        {it.is_published && <Badge className="bg-cyan-50 text-cyan-700 border border-cyan-200 text-[9px]">Published</Badge>}
                                                        {it.is_promoted && <Badge className="bg-amber-50 text-amber-700 border border-amber-200 text-[9px]"><Star size={8} className="mr-0.5" /> Featured</Badge>}
                                                    </div>
                                                    <div className="flex gap-3 text-xs text-zinc-400">
                                                        <span className="flex items-center gap-1"><Clock size={11} /> {it.duration_days}d</span>
                                                        <span className="flex items-center gap-1"><DollarSign size={11} /> ${it.total_price || 0}</span>
                                                        <span>{fmt(it.created_at)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ===== MESSAGING ===== */}
                    {(activeView === 'messages' || activeView === 'chat') && isLeadsAgent && (
                        <div data-testid="team-messages-view" className="flex rounded-2xl border border-zinc-200 bg-white overflow-hidden" style={{ height: 'calc(100vh - 80px)' }}>
                            <div className="w-[300px] border-r border-zinc-200 flex flex-col shrink-0">
                                <div className="p-4 border-b border-zinc-100">
                                    <h2 className="text-base font-semibold text-zinc-900">Messages</h2>
                                    <p className="text-xs text-zinc-400 mt-0.5">{conversations.length} conversations</p>
                                </div>
                                <div className="flex-1 overflow-y-auto">
                                    {conversations.length === 0 ? (
                                        <div className="text-center py-16 px-4">
                                            <MessageCircle className="mx-auto mb-3 text-zinc-200" size={28} />
                                            <p className="text-xs text-zinc-400">No conversations yet</p>
                                        </div>
                                    ) : conversations.map(conv => (
                                        <button key={conv.id} onClick={() => openConversation(conv)}
                                            className={`w-full text-left px-4 py-3 border-b border-zinc-50 hover:bg-zinc-50 transition ${activeConversation?.id === conv.id ? 'bg-zinc-50' : ''}`}
                                            data-testid={`team-conv-${conv.id}`}>
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-bold text-xs flex-shrink-0">
                                                    {(conv.other_user?.name || 'U').charAt(0).toUpperCase()}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-zinc-900 truncate">{conv.other_user?.name || 'User'}</p>
                                                    <p className="text-xs text-zinc-400 truncate">{conv.last_message?.content || 'No messages'}</p>
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="flex-1 flex flex-col">
                                {!activeConversation ? (
                                    <div className="flex-1 flex items-center justify-center">
                                        <div className="text-center">
                                            <MessageCircle size={40} className="mx-auto text-zinc-200 mb-3" />
                                            <p className="text-zinc-400 text-sm">Select a conversation</p>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="px-5 py-3 border-b border-zinc-100 flex items-center gap-3">
                                            <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-bold text-xs">
                                                {(activeConversation.other_user?.name || 'U').charAt(0).toUpperCase()}
                                            </div>
                                            <div>
                                                <p className="text-sm font-semibold text-zinc-900">{activeConversation.other_user?.name}</p>
                                                <p className="text-[10px] text-zinc-400">Traveler</p>
                                            </div>
                                        </div>
                                        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                                            {messages.map((msg, i) => {
                                                const isMine = msg.sender_id !== activeConversation.other_user?.id;
                                                return (
                                                    <div key={msg.id || i} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                                                        <div className={`max-w-[70%] px-4 py-2.5 rounded-2xl text-sm ${
                                                            isMine ? 'bg-zinc-900 text-white rounded-br-md' : 'bg-zinc-100 text-zinc-900 rounded-bl-md'
                                                        }`}>
                                                            <p>{msg.content}</p>
                                                            <p className={`text-[10px] mt-1 ${isMine ? 'text-zinc-400' : 'text-zinc-400'}`}>{msg.created_at ? format(new Date(msg.created_at), 'HH:mm') : ''}</p>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            <div ref={msgEndRef} />
                                        </div>
                                        <div className="px-4 py-3 border-t border-zinc-100 flex gap-2">
                                            <input
                                                type="text" value={newMessage} onChange={e => setNewMessage(e.target.value)}
                                                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                                                placeholder="Type a message..."
                                                className="flex-1 px-4 py-2.5 rounded-xl border border-zinc-200 text-sm focus:border-zinc-400 focus:ring-0"
                                                data-testid="team-msg-input"
                                            />
                                            <button onClick={sendMessage} disabled={sendingMsg || !newMessage.trim()}
                                                className="bg-zinc-900 text-white rounded-xl px-4 py-2.5 hover:bg-zinc-800 disabled:opacity-40 transition" data-testid="team-msg-send">
                                                <Send size={16} />
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}
